import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../services/firebase";
import Layout from "../component/Layout";
import {
  Search,
  SlidersHorizontal,
  Eye,
  CheckCircle,
  Clock,
  CreditCard,
  RefreshCw,
  AlertCircle,
  FileText,
  Package,
  X,
} from "lucide-react";
import "./Buyers.css";

/* ── Helpers ─────────────────────────────────────────────── */
const fmt = (n) => {
  if (!n || n === 0) return "₹0";
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${n}`;
};

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const GRADIENT_POOL = [
  "linear-gradient(135deg,#8a6b32,#c4973e)",
  "linear-gradient(135deg,#1d4ed8,#3b82f6)",
  "linear-gradient(135deg,#065f46,#10b981)",
  "linear-gradient(135deg,#7c3aed,#a78bfa)",
  "linear-gradient(135deg,#b45309,#f59e0b)",
  "linear-gradient(135deg,#dc2626,#f87171)",
];
const getGradient = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return GRADIENT_POOL[Math.abs(h) % GRADIENT_POOL.length];
};

const isPdfUrl = (url = "") => {
  if (!url || typeof url !== "string") return false;
  const lower = url.toLowerCase();
  return (
    lower.includes(".pdf") ||
    lower.includes("/raw/upload/") ||
    lower.includes("format=pdf") ||
    lower.includes("content_type=application/pdf") ||
    lower.match(/\.pdf(\?.*)?$/i) !== null
  );
};

// Derive verification & account status from raw buyer doc
const deriveStatus = (buyerDoc, regDoc) => {
  const rawStatus = (buyerDoc.status || "pending").toLowerCase();
  let accountStatus = rawStatus;
  let verificationStatus = "pending";

  if (regDoc) {
    const hasGstin = !!regDoc.gstinNumber;
    if (rawStatus === "active" || rawStatus === "verified") {
      verificationStatus = hasGstin ? "verified" : "kyc_review";
    } else if (rawStatus === "suspended" || rawStatus === "blocked") {
      verificationStatus = hasGstin ? "verified" : "pending";
      accountStatus = rawStatus;
    } else {
      verificationStatus = hasGstin ? "kyc_review" : "pending";
    }
  }
  return { accountStatus, verificationStatus };
};

/* ── Component ───────────────────────────────────────────── */
const Buyers = () => {
  const navigate = useNavigate();

  // Raw data from Firestore
  const [rawBuyers, setRawBuyers] = useState([]);
  const [rawRegistrations, setRawRegistrations] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [orderCounts, setOrderCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ordersToday, setOrdersToday] = useState(0);

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [verifFilter, setVerifFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [docFilter, setDocFilter] = useState("all");
  const [kpiFilter, setKpiFilter] = useState(null);

  /* ── 1. Fetch buyers collection (real-time) ── */
  useEffect(() => {
    const q = query(collection(db, "buyers"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRawBuyers(docs);
        setLoading(false);
      },
      (err) => {
        console.error("[Buyers] Firestore error:", err);
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  /* ── 2. Fetch buyerregistrations and sellerregistrations in real-time ── */
  useEffect(() => {
    const unsub1 = onSnapshot(
      collection(db, "buyerregistrations"),
      (snap) => {
        const map = {};
        const regs = [];
        snap.docs.forEach((d) => {
          const data = d.data();
          regs.push({ id: d.id, ...data });
          map[d.id] = data;
          if (data.buyerId) map[data.buyerId] = data;
          if (data.sellerId) map[data.sellerId] = data;
          if (data.organizationContact) map[data.organizationContact] = data;
          if (data.transactionalMobile) map[data.transactionalMobile] = data;
        });
        setProfiles((prev) => ({ ...prev, ...map }));
        setRawRegistrations(regs);
      },
      (err) => {
        console.error("[Buyers] buyerregistrations error:", err);
      }
    );

    const unsub2 = onSnapshot(
      collection(db, "sellerregistrations"),
      (snap) => {
        const map = {};
        snap.docs.forEach((d) => {
          const data = d.data();
          map[d.id] = data;
          if (data.buyerId) map[data.buyerId] = data;
          if (data.sellerId) map[data.sellerId] = data;
          if (data.organizationContact) map[data.organizationContact] = data;
          if (data.transactionalMobile) map[data.transactionalMobile] = data;
        });
        setProfiles((prev) => ({ ...map, ...prev }));
      },
      (err) => {
        console.error("[Buyers] sellerregistrations error:", err);
      }
    );

    return () => {
      unsub1();
      unsub2();
    };
  }, []);

  /* ── 3. Fetch order counts per buyer ── */
  useEffect(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const unsub = onSnapshot(
      collection(db, "orderList"),
      (snap) => {
        const counts = {};
        let todayCount = 0;
        snap.docs.forEach((d) => {
          const data = d.data();
          const bid = data.buyerId;
          if (!bid) return;
          counts[bid] = (counts[bid] || 0) + 1;
          const created = data.createdAt?.toDate?.();
          if (created && created >= todayStart) todayCount++;
        });
        setOrderCounts(counts);
        setOrdersToday(todayCount);
      },
      (err) => console.error("[Buyers] orderList error:", err)
    );
    return () => unsub();
  }, []);

  /* ── 4. Merge rawBuyers & rawRegistrations into enriched list ── */
  const enrichedBuyers = useMemo(() => {
    const seenIds = new Set();
    const result = [];

    const processBuyer = (b, regFromMap = null) => {
      const bid = b.buyerId || b.id;
      if (!bid || seenIds.has(bid)) return;
      seenIds.add(bid);

      const reg =
        regFromMap ||
        profiles[bid] ||
        profiles[b.id] ||
        profiles[b.buyerId] ||
        profiles[b.mobile] ||
        null;

      const { accountStatus, verificationStatus } = deriveStatus(b, reg);

      const name = (reg?.organizationName || b.organizationName || b.name || "Unnamed Buyer").trim();
      const city = (reg?.contactCity || reg?.shippingLocation?.address?.city || b.city || "—").trim();
      const state = (reg?.contactState || reg?.shippingLocation?.address?.state || b.state || "—").trim();
      const pinCode = (reg?.contactPinCode || reg?.shippingLocation?.address?.postalCode || b.pinCode || "").trim();
      const district = (reg?.contactDistrict || reg?.shippingLocation?.address?.district || "").trim();
      const streetAddress = (reg?.streetAddress || "").trim();
      const buildingName = (reg?.buildingName || "").trim();
      const shopNumber = (reg?.shopNumber || "").trim();
      const addressString = (reg?.shippingLocation?.addressString || "").trim();

      const mobile = (reg?.organizationContact || reg?.transactionalMobile || b.mobile || b.phone || "—").trim();
      const email = (reg?.organizationEmail || reg?.transactionalEmail || b.email || "").trim();
      const gstin = (reg?.gstinNumber || b.gstin || "").trim();
      const gstrStatus = (reg?.gstrStatus || "registered").trim();
      const buyerType = (reg?.businessType || b.businessType || "Retailer").trim();
      const teamSize = (reg?.teamSize || "—").trim();

      const bankName = (reg?.bankName || "").trim();
      const accountNumber = (reg?.accountNumber || "").trim();
      const ifscCode = (reg?.ifscCode || "").trim();
      const accountHolderName = (reg?.accountHolderName || "").trim();
      const jmiOfficerID = (reg?.jmiOfficerID || "").trim();

      const dealingPersons = Array.isArray(reg?.dealingPersons) ? reg.dealingPersons : [];
      const primaryPerson = dealingPersons[0] || null;
      const owner = (primaryPerson?.fullName || b.owner || "").trim();
      const ownerRole = (primaryPerson?.role || "").trim();
      const ownerDept = (primaryPerson?.department || "").trim();

      // Document IDs and URLs
      const bizDocIds = Array.isArray(reg?.businessDocumentIds) ? reg.businessDocumentIds : [];
      const bizDocUrls = Array.isArray(reg?.businessDocumentUrls) ? reg.businessDocumentUrls : [];
      const orgPhotoIds = Array.isArray(reg?.organizationPhotoIds) ? reg.organizationPhotoIds : [];
      const orgPhotoUrls = Array.isArray(reg?.organizationPhotoUrls) ? reg.organizationPhotoUrls : [];

      const allDocIds = [...bizDocIds, ...orgPhotoIds];
      const allDocUrls = [
        ...allDocIds,
        ...bizDocUrls,
        ...orgPhotoUrls,
        reg?.gstCertificateUrl,
        b?.gstCertificateUrl,
        reg?.panCardUrl,
        b?.panCardUrl,
        reg?.cancelledChequeUrl,
        b?.cancelledChequeUrl,
        ...(Array.isArray(reg?.documents) ? reg.documents.map((d) => d?.url || d?.name) : []),
      ].filter(Boolean);

      const pdfUrls = allDocUrls.filter((u) => isPdfUrl(u));
      const hasPdf = pdfUrls.length > 0;
      const pdfCount = pdfUrls.length;
      const photoCount = orgPhotoIds.length || orgPhotoUrls.length;
      const docCount = allDocUrls.length;

      const dealingNames = dealingPersons
        .map((p) => p?.fullName || "")
        .filter(Boolean)
        .join(" ");
      const dealingPhones = dealingPersons
        .map((p) => p?.contactNumber || "")
        .filter(Boolean)
        .join(" ");
      const dealingEmails = dealingPersons
        .map((p) => p?.email || "")
        .filter(Boolean)
        .join(" ");

      result.push({
        id: bid,
        mobile,
        name,
        initials: getInitials(name),
        city,
        state,
        pinCode,
        district,
        streetAddress,
        buildingName,
        shopNumber,
        addressString,
        email,
        gstin,
        gstrStatus,
        buyerType,
        teamSize,
        owner,
        ownerRole,
        ownerDept,
        bankName,
        accountNumber,
        ifscCode,
        accountHolderName,
        jmiOfficerID,
        dealingPersons,
        dealingNames,
        dealingPhones,
        dealingEmails,
        bizDocIds,
        bizDocUrls,
        hasPdf,
        pdfCount,
        photoCount,
        docCount,
        allDocIds,
        allDocUrls,
        accountStatus,
        verificationStatus,
        creditStatus: b.creditStatus || "disabled",
        creditLimit: b.creditLimit || 0,
        orders: orderCounts[bid] || 0,
        outstanding: b.outstanding || 0,
        createdAt: b.createdAt || reg?.createdAt || reg?.updatedAt,
        _raw: b,
        _reg: reg,
      });
    };

    // First process all buyers in rawBuyers
    rawBuyers.forEach((b) => processBuyer(b));

    // Then process any registration not already present
    rawRegistrations.forEach((r) => {
      const regId = r.buyerId || r.id;
      if (!seenIds.has(regId)) {
        processBuyer({ id: regId, ...r }, r);
      }
    });

    return result;
  }, [rawBuyers, rawRegistrations, profiles, orderCounts]);

  /* ── KPI counts ── */
  const totalCount = enrichedBuyers.length;
  const activeCount = enrichedBuyers.filter(
    (b) => b.accountStatus === "active" || b.accountStatus === "verified"
  ).length;
  const pendingKycCount = enrichedBuyers.filter(
    (b) =>
      b.verificationStatus === "pending" ||
      b.verificationStatus === "kyc_review"
  ).length;
  const suspendedCount = enrichedBuyers.filter(
    (b) => b.accountStatus === "suspended"
  ).length;
  const creditCount = enrichedBuyers.filter(
    (b) => b.creditStatus === "enabled"
  ).length;

  /* ── Comprehensive multi-token search & multi-filter logic ── */
  const filtered = useMemo(() => {
    const rawQ = search.trim().toLowerCase();
    const tokens = rawQ ? rawQ.split(/\s+/).filter(Boolean) : [];

    return enrichedBuyers.filter((b) => {
      // 1. Multi-token Search Matching: All tokens must match at least one field
      if (tokens.length > 0) {
        const matchAllTokens = tokens.every((token) => {
          const tokenDigits = token.replace(/\D/g, "");
          const bMobileDigits = b.mobile ? b.mobile.replace(/\D/g, "") : "";

          return (
            (b.name && b.name.toLowerCase().includes(token)) ||
            (b.id && b.id.toLowerCase().includes(token)) ||
            (b.mobile && b.mobile.toLowerCase().includes(token)) ||
            (tokenDigits.length >= 3 && bMobileDigits.includes(tokenDigits)) ||
            (b.email && b.email.toLowerCase().includes(token)) ||
            (b.gstin && b.gstin.toLowerCase().includes(token)) ||
            (b.city && b.city.toLowerCase().includes(token)) ||
            (b.state && b.state.toLowerCase().includes(token)) ||
            (b.district && b.district.toLowerCase().includes(token)) ||
            (b.pinCode && b.pinCode.toLowerCase().includes(token)) ||
            (b.buildingName && b.buildingName.toLowerCase().includes(token)) ||
            (b.streetAddress && b.streetAddress.toLowerCase().includes(token)) ||
            (b.shopNumber && b.shopNumber.toLowerCase().includes(token)) ||
            (b.addressString && b.addressString.toLowerCase().includes(token)) ||
            (b.owner && b.owner.toLowerCase().includes(token)) ||
            (b.ownerRole && b.ownerRole.toLowerCase().includes(token)) ||
            (b.ownerDept && b.ownerDept.toLowerCase().includes(token)) ||
            (b.dealingNames && b.dealingNames.toLowerCase().includes(token)) ||
            (tokenDigits.length >= 3 &&
              b.dealingPhones &&
              b.dealingPhones.replace(/\D/g, "").includes(tokenDigits)) ||
            (b.dealingEmails && b.dealingEmails.toLowerCase().includes(token)) ||
            (b.buyerType && b.buyerType.toLowerCase().includes(token)) ||
            (b.bankName && b.bankName.toLowerCase().includes(token)) ||
            (b.ifscCode && b.ifscCode.toLowerCase().includes(token)) ||
            (b.accountHolderName && b.accountHolderName.toLowerCase().includes(token)) ||
            (tokenDigits.length >= 3 &&
              b.accountNumber &&
              b.accountNumber.includes(tokenDigits)) ||
            (b.jmiOfficerID && b.jmiOfficerID.toLowerCase().includes(token)) ||
            // Semantic intent matching
            (token === "pdf" && b.hasPdf) ||
            (token === "gst" && (Boolean(b.gstin) || b.hasPdf)) ||
            ((token === "doc" || token === "docs" || token === "document" || token === "documents" || token === "kyc") && b.docCount > 0) ||
            ((token === "photo" || token === "photos" || token === "image" || token === "images") && b.photoCount > 0) ||
            ((token === "bank" || token === "account") && Boolean(b.bankName || b.accountNumber)) ||
            ((token === "credit") && b.creditStatus === "enabled") ||
            // Document IDs or URLs match
            (b.allDocIds && b.allDocIds.some((id) => id.toLowerCase().includes(token))) ||
            (b.allDocUrls && b.allDocUrls.some((u) => u.toLowerCase().includes(token)))
          );
        });

        if (!matchAllTokens) return false;
      }

      // 2. Account Status Filter
      const matchStatus =
        statusFilter === "all" ||
        b.accountStatus === statusFilter ||
        (statusFilter === "active" && (b.accountStatus === "active" || b.accountStatus === "verified"));

      // 3. KYC Verification Filter
      const matchVerif =
        verifFilter === "all" || b.verificationStatus === verifFilter;

      // 4. Buyer Type Filter
      const matchType =
        typeFilter === "all" ||
        (b.buyerType || "").toLowerCase().includes(typeFilter.toLowerCase());

      // 5. Document Filter
      const matchDoc =
        docFilter === "all" ||
        (docFilter === "pdf" && b.hasPdf) ||
        (docFilter === "photo" && b.photoCount > 0) ||
        (docFilter === "has_docs" && b.docCount > 0) ||
        (docFilter === "no_docs" && b.docCount === 0);

      // 6. KPI Card Quick-Filter
      const matchKpi =
        !kpiFilter ||
        (kpiFilter === "active" &&
          (b.accountStatus === "active" || b.accountStatus === "verified")) ||
        (kpiFilter === "pending" &&
          (b.verificationStatus === "pending" ||
            b.verificationStatus === "kyc_review")) ||
        (kpiFilter === "suspended" && b.accountStatus === "suspended") ||
        (kpiFilter === "credit" && b.creditStatus === "enabled");

      return matchStatus && matchVerif && matchType && matchDoc && matchKpi;
    });
  }, [enrichedBuyers, search, statusFilter, verifFilter, typeFilter, docFilter, kpiFilter]);

  const handleKpiClick = (filterKey) => {
    if (kpiFilter === filterKey) {
      setKpiFilter(null);
      setStatusFilter("all");
      setVerifFilter("all");
    } else {
      setKpiFilter(filterKey);
      if (filterKey === "active") setStatusFilter("active");
      else if (filterKey === "pending") setVerifFilter("kyc_review");
      else if (filterKey === "suspended") setStatusFilter("suspended");
      else if (filterKey === "credit") {
        setStatusFilter("all");
        setVerifFilter("all");
      }
    }
  };

  const verifLabel = {
    verified: "VERIFIED",
    kyc_review: "KYC REVIEW",
    pending: "PENDING",
  };

  const verifClass = {
    verified: "verif-verified",
    kyc_review: "verif-kyc-review",
    pending: "verif-pending",
  };

  const accountPillClass = {
    active: "pill-active",
    verified: "pill-active",
    pending: "pill-pending",
    suspended: "pill-suspended",
    blocked: "pill-blocked",
  };

  /* ── Render ── */
  return (
    <Layout pageTitle="Buyers">
      <div className="buyers-page">
        {/* ── Header ── */}
        <div className="buyers-header">
          <div className="buyers-header-left">
            <h2>Buyers</h2>
            <p>Manage JMI's registered B2B buyers — live from Firestore</p>
          </div>
          <div className="buyers-header-actions">
            <button className="btn-outline" onClick={() => window.location.reload()}>
              <RefreshCw size={15} /> Refresh
            </button>
          </div>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div className="buyers-error-banner">
            <AlertCircle size={16} />
            <span>Firestore error: {error}. Check security rules or login session.</span>
          </div>
        )}

        {/* ── KPI Cards ── */}
        <div className="buyers-kpi-grid">
          <div
            className={`buyers-kpi-card kpi-card-total`}
            onClick={() => {
              setKpiFilter(null);
              setStatusFilter("all");
              setVerifFilter("all");
            }}
          >
            <div className="kpi-label">Total Buyers</div>
            <div className="kpi-value">{loading ? "—" : totalCount.toLocaleString()}</div>
            <div className="kpi-hint">All registered</div>
          </div>
          <div
            className={`buyers-kpi-card kpi-card-active ${kpiFilter === "active" ? "active-filter" : ""}`}
            onClick={() => handleKpiClick("active")}
          >
            <div className="kpi-label">Active Buyers</div>
            <div className="kpi-value">{loading ? "—" : activeCount}</div>
            <div className="kpi-hint">Account active / verified</div>
          </div>
          <div
            className={`buyers-kpi-card kpi-card-pending ${kpiFilter === "pending" ? "active-filter" : ""}`}
            onClick={() => handleKpiClick("pending")}
          >
            <div className="kpi-label">Pending KYC</div>
            <div className="kpi-value">{loading ? "—" : pendingKycCount}</div>
            <div className="kpi-hint">Awaiting verification</div>
          </div>
          <div
            className={`buyers-kpi-card kpi-card-suspended ${kpiFilter === "suspended" ? "active-filter" : ""}`}
            onClick={() => handleKpiClick("suspended")}
          >
            <div className="kpi-label">Suspended</div>
            <div className="kpi-value">{loading ? "—" : suspendedCount}</div>
            <div className="kpi-hint">Temporarily halted</div>
          </div>
          <div
            className={`buyers-kpi-card kpi-card-credit ${kpiFilter === "credit" ? "active-filter" : ""}`}
            onClick={() => handleKpiClick("credit")}
          >
            <div className="kpi-label">Credit Enabled</div>
            <div className="kpi-value">{loading ? "—" : creditCount}</div>
            <div className="kpi-hint">With credit line</div>
          </div>
          <div className="buyers-kpi-card kpi-card-orders">
            <div className="kpi-label">Orders Today</div>
            <div className="kpi-value">{ordersToday}</div>
            <div className="kpi-hint">Placed today</div>
          </div>
        </div>

        {/* ── Enhanced Filters Toolbar ── */}
        <div className="sellers-toolbar-container">
          <div className="sellers-main-toolbar">
            {/* Search Input Box */}
            <div className="sellers-search-box">
              <Search size={16} style={{ color: "#8a6b32", flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Search firm, buyer ID, contact, mobile, GSTIN, city, bank, PDF..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <span className="sellers-search-count-pill">
                  {filtered.length} found
                </span>
              )}
              {search && (
                <button
                  className="clear-search-btn"
                  onClick={() => setSearch("")}
                  title="Clear search"
                >
                  <X size={15} />
                </button>
              )}
            </div>

            {/* Filter Dropdowns */}
            <div className="sellers-filter-group">
              <SlidersHorizontal size={15} style={{ color: "#64748b" }} />

              {/* Account: All */}
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setKpiFilter(null);
                }}
                className={`filter-select-enhanced ${statusFilter !== "all" ? "active" : ""}`}
                title="Account Status"
              >
                <option value="all">Account: All</option>
                <option value="active">Active / Verified</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
                <option value="blocked">Blocked</option>
              </select>

              {/* KYC: All */}
              <select
                value={verifFilter}
                onChange={(e) => {
                  setVerifFilter(e.target.value);
                  setKpiFilter(null);
                }}
                className={`filter-select-enhanced ${verifFilter !== "all" ? "active" : ""}`}
                title="KYC Verification Status"
              >
                <option value="all">KYC: All</option>
                <option value="verified">Verified</option>
                <option value="kyc_review">KYC Review</option>
                <option value="pending">Pending</option>
              </select>

              {/* Type: All */}
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className={`filter-select-enhanced ${typeFilter !== "all" ? "active" : ""}`}
                title="Buyer Type"
              >
                <option value="all">Type: All</option>
                <option value="Retailer">Retailer</option>
                <option value="Wholesaler">Wholesaler</option>
                <option value="Manufacturer">Manufacturer</option>
                <option value="Bullion">Bullion Dealer</option>
              </select>

              {/* Docs: All */}
              <select
                value={docFilter}
                onChange={(e) => setDocFilter(e.target.value)}
                className={`filter-select-enhanced ${docFilter !== "all" ? "active" : ""}`}
                title="Document Attachments"
              >
                <option value="all">Docs: All</option>
                <option value="pdf">Has PDF Certificates</option>
                <option value="photo">Has Photos</option>
                <option value="has_docs">Any Documents Attached</option>
                <option value="no_docs">Missing Documents</option>
              </select>
            </div>
          </div>

          {/* Quick Search Chips */}
          <div className="sellers-quick-chips">
            <span className="sellers-quick-label">Quick Filters:</span>
            <button
              className={`sellers-chip ${docFilter === "pdf" ? "active" : ""}`}
              onClick={() => setDocFilter(docFilter === "pdf" ? "all" : "pdf")}
            >
              <FileText size={12} /> Has GST PDF
            </button>
            <button
              className={`sellers-chip ${typeFilter === "Retailer" ? "active" : ""}`}
              onClick={() => setTypeFilter(typeFilter === "Retailer" ? "all" : "Retailer")}
            >
              🏪 Retailers
            </button>
            <button
              className={`sellers-chip ${verifFilter === "kyc_review" ? "active" : ""}`}
              onClick={() => setVerifFilter(verifFilter === "kyc_review" ? "all" : "kyc_review")}
            >
              ⏳ Pending KYC
            </button>
            <button
              className={`sellers-chip ${statusFilter === "active" ? "active" : ""}`}
              onClick={() => setStatusFilter(statusFilter === "active" ? "all" : "active")}
            >
              ✓ Verified Buyers
            </button>
            <button
              className={`sellers-chip ${docFilter === "photo" ? "active" : ""}`}
              onClick={() => setDocFilter(docFilter === "photo" ? "all" : "photo")}
            >
              📸 With Photos
            </button>
          </div>

          {/* Active Filter Tags Bar */}
          {(search.trim() || statusFilter !== "all" || verifFilter !== "all" || typeFilter !== "all" || docFilter !== "all" || kpiFilter) && (
            <div className="sellers-active-bar">
              <span style={{ fontWeight: 600, color: "#64748b", fontSize: "11px" }}>
                Active Filters:
              </span>
              {search.trim() && (
                <span className="sellers-active-tag">
                  Search: "{search.trim()}"
                  <button onClick={() => setSearch("")} title="Clear search text">
                    <X size={11} />
                  </button>
                </span>
              )}
              {statusFilter !== "all" && (
                <span className="sellers-active-tag">
                  Account: {statusFilter}
                  <button onClick={() => setStatusFilter("all")} title="Remove account filter">
                    <X size={11} />
                  </button>
                </span>
              )}
              {verifFilter !== "all" && (
                <span className="sellers-active-tag">
                  KYC: {verifFilter}
                  <button onClick={() => setVerifFilter("all")} title="Remove KYC filter">
                    <X size={11} />
                  </button>
                </span>
              )}
              {typeFilter !== "all" && (
                <span className="sellers-active-tag">
                  Type: {typeFilter}
                  <button onClick={() => setTypeFilter("all")} title="Remove type filter">
                    <X size={11} />
                  </button>
                </span>
              )}
              {docFilter !== "all" && (
                <span className="sellers-active-tag">
                  Docs: {docFilter === "pdf" ? "Has PDF" : docFilter === "photo" ? "Has Photos" : docFilter === "has_docs" ? "Any Docs" : "Missing Docs"}
                  <button onClick={() => setDocFilter("all")} title="Remove docs filter">
                    <X size={11} />
                  </button>
                </span>
              )}
              {kpiFilter && (
                <span className="sellers-active-tag">
                  KPI: {kpiFilter}
                  <button onClick={() => setKpiFilter(null)} title="Remove KPI filter">
                    <X size={11} />
                  </button>
                </span>
              )}
              <button
                className="btn-clear-all"
                onClick={() => {
                  setSearch("");
                  setStatusFilter("all");
                  setVerifFilter("all");
                  setTypeFilter("all");
                  setDocFilter("all");
                  setKpiFilter(null);
                }}
              >
                Clear All
              </button>
            </div>
          )}
        </div>

        {/* ── Table Card ── */}
        <div className="buyers-table-card">
          <div className="table-meta-bar">
            <span className="results-count">
              Showing <strong>{filtered.length}</strong> of <strong>{totalCount}</strong> buyers
            </span>
          </div>

          {loading ? (
            <div className="buyers-loading">
              <div className="buyers-spinner" />
              <p>Fetching buyers live from Firestore…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="buyers-empty">
              <Package size={40} style={{ color: "#94a3b8", marginBottom: 10 }} />
              <h4>No buyers found</h4>
              <p>Try adjusting your search query or filters.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="buyers-table">
                <thead>
                  <tr>
                    <th>Buyer ID</th>
                    <th>Firm / Organization</th>
                    <th>Contact Person</th>
                    <th>Mobile</th>
                    <th>Location</th>
                    <th>Type</th>
                    <th>Documents</th>
                    <th>Verification</th>
                    <th style={{ textAlign: "center" }}>Orders</th>
                    <th style={{ textAlign: "right" }}>Outstanding</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((buyer) => (
                    <tr
                      key={buyer.id}
                      className="buyer-row"
                      onClick={() => navigate(`/buyers/${buyer.id}`)}
                    >
                      {/* Buyer ID */}
                      <td>
                        <span className="buyer-id-cell">{buyer.id}</span>
                      </td>

                      {/* Firm & GSTIN */}
                      <td>
                        <div className="buyer-name-cell">
                          <div
                            className="buyer-avatar"
                            style={{ background: getGradient(buyer.name) }}
                          >
                            {buyer.initials}
                          </div>
                          <div>
                            <div className="buyer-name">{buyer.name}</div>
                            {buyer.gstin ? (
                              <div className="buyer-owner" style={{ color: "#065f46", fontWeight: 600 }}>
                                GSTIN: {buyer.gstin}
                              </div>
                            ) : (
                              <div className="buyer-owner" style={{ color: "#94a3b8" }}>
                                No GSTIN
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Contact Person */}
                      <td>
                        {buyer.owner ? (
                          <div>
                            <div style={{ fontWeight: 600, fontSize: "12px", color: "#1e293b" }}>
                              {buyer.owner}
                            </div>
                            {buyer.ownerRole && (
                              <div style={{ fontSize: "11px", color: "#64748b" }}>
                                {buyer.ownerRole}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: "#94a3b8", fontSize: "12px" }}>—</span>
                        )}
                      </td>

                      {/* Mobile */}
                      <td>
                        <span style={{ fontFamily: "monospace", fontSize: "12px", color: "#475569" }}>
                          {buyer.mobile}
                        </span>
                      </td>

                      {/* Location */}
                      <td>
                        <div className="location-col">
                          <span className="city">{buyer.city}</span>
                          <span className="state">{buyer.state}</span>
                        </div>
                      </td>

                      {/* Type */}
                      <td>
                        <span style={{ fontSize: "12px", color: "#475569", fontWeight: 500 }}>
                          {buyer.buyerType || "—"}
                        </span>
                      </td>

                      {/* Attached Documents */}
                      <td>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                          {buyer.hasPdf && (
                            <span className="seller-doc-badge-pill pdf" title="GST / Business Proof PDF Attached">
                              <FileText size={10} /> PDF
                            </span>
                          )}
                          {buyer.photoCount > 0 && (
                            <span className="seller-doc-badge-pill photo" title={`${buyer.photoCount} Store / Office Photos`}>
                              📸 {buyer.photoCount}
                            </span>
                          )}
                          {!buyer.hasPdf && buyer.photoCount === 0 && (
                            <span style={{ color: "#94a3b8", fontSize: "11px" }}>None</span>
                          )}
                        </div>
                      </td>

                      {/* Verification */}
                      <td>
                        <span className={`verif-badge ${verifClass[buyer.verificationStatus] || "verif-pending"}`}>
                          {buyer.verificationStatus === "verified" && <CheckCircle size={11} />}
                          {buyer.verificationStatus === "kyc_review" && <Clock size={11} />}
                          {verifLabel[buyer.verificationStatus] || buyer.verificationStatus}
                        </span>
                      </td>

                      {/* Orders */}
                      <td className="orders-col" style={{ textAlign: "center" }}>
                        {buyer.orders > 0 ? buyer.orders : <span style={{ color: "#94a3b8" }}>0</span>}
                      </td>

                      {/* Outstanding */}
                      <td className="outstanding-col">
                        {buyer.outstanding > 0 ? (
                          <span className="amount">{fmt(buyer.outstanding)}</span>
                        ) : (
                          <span className="zero">₹0</span>
                        )}
                      </td>

                      {/* Status */}
                      <td>
                        <div className="status-dims">
                          <span className={`status-pill-sm ${accountPillClass[buyer.accountStatus] || "pill-pending"}`}>
                            {(buyer.accountStatus || "PENDING").toUpperCase()}
                          </span>
                          {buyer.creditStatus === "enabled" && (
                            <span className="status-pill-sm" style={{ background: "#eff6ff", color: "#1d4ed8" }}>
                              <CreditCard size={9} /> CREDIT
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Action */}
                      <td onClick={(e) => e.stopPropagation()}>
                        {buyer.verificationStatus === "pending" || buyer.verificationStatus === "kyc_review" ? (
                          <button className="btn-review-buyer" onClick={() => navigate(`/buyers/${buyer.id}`)}>
                            <Eye size={13} /> Review KYC
                          </button>
                        ) : (
                          <button className="btn-view-buyer" onClick={() => navigate(`/buyers/${buyer.id}`)}>
                            <Eye size={13} /> 360° View
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Buyers;
